import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import CommunicationThreadModal from '@/modules/admin-dashboard/components/modals/CommunicationThreadModal.tsx';
import { CreateCommunicationHistoryRecordModal } from '@/modules/admin-dashboard/components/modals/CreateCommunicationHistoryRecordModal.tsx';
import { CreateCommunicationModal } from '@/modules/admin-dashboard/components/modals/CreateCommunicationModal.tsx';
import {
  CommunicationRecipientDetail,
  CommunicationThread,
  CommunicationThreadFilters,
  useCommunicationsQuery,
} from '@/modules/admin-dashboard/queries/communications.ts';
import {
  Badge,
  Button,
  FilterItem,
  InfoCard,
  ModalProviderContext,
  SearchField,
  SelectOption,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { CopyIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';

export interface CommunicationClient {
  id: number;
  name: string;
  email?: string | null;
  correspondence_email?: string | null;
}

interface CommunicationsWorkspaceProps {
  client?: CommunicationClient;
}

interface CommunicationBusiness {
  id: number;
  name: string;
  correspondence_email?: string | null;
}

type CommunicationFilterType =
  | 'email'
  | 'physical_mail'
  | 'phone_call'
  | 'live_meeting'
  | 'platform_notification'
  | 'external';

interface Filters {
  text?: string;
  type?: CommunicationFilterType;
  sender?: string;
  recipient?: string;
}

const mapCommunicationStatusToBadge: Record<string, ReactNode> = {
  pending: (
    <Badge className="w-fit bg-[#0369A1] !justify-start text-white text-xs">In Progress</Badge>
  ),
  sent: <Badge className="w-fit bg-[#16A34A] !justify-start text-white text-xs">Completed</Badge>,
  failed: <Badge className="w-fit bg-[#B91C1C] !justify-start text-white text-xs">Failed</Badge>,
};

const typeOptions: SelectOption<CommunicationFilterType>[] = [
  { value: 'email', label: 'Sent email' },
  { value: 'physical_mail', label: 'Physical mail' },
  { value: 'phone_call', label: 'Phone call' },
  { value: 'live_meeting', label: 'In-person meeting' },
  { value: 'platform_notification', label: 'Platform notification' },
  { value: 'external', label: 'Received email' },
];

const headItems: TableHeadColumn[] = [
  { title: 'Subject', className: 'min-w-[250px] !pr-3' },
  { title: 'Sender', className: '!px-3' },
  { title: 'Date & Time', className: 'min-w-[150px] !px-3' },
  { title: 'Type', className: '!px-3' },
  { title: 'Status', className: '!px-3' },
  { title: 'Participants', className: '!px-3 min-w-[420px]' },
];

const getThreadFilterType = (thread: CommunicationThread): CommunicationFilterType => {
  if (thread.thread_type === 'external') return 'external';
  if (thread.communication_origin === 'platform_notification') {
    return 'platform_notification';
  }
  if (thread.correspondence_type === 'physical_mail') return 'physical_mail';
  if (thread.correspondence_type === 'phone_call') return 'phone_call';
  if (thread.correspondence_type === 'live_meeting') return 'live_meeting';
  return 'email';
};

const getThreadDisplayType = (thread: CommunicationThread) => {
  const filterType = getThreadFilterType(thread);
  if (filterType === 'platform_notification') return 'Platform notification';
  if (filterType === 'physical_mail') return 'Physical mail';
  if (filterType === 'phone_call') return 'Phone call';
  if (filterType === 'live_meeting') return 'In-person meeting';
  if (filterType === 'external') return 'Received email';
  return 'Sent email';
};

const getRecipientLabels = (thread: CommunicationThread) => {
  if (thread.recipient_details?.length) {
    return thread.recipient_details.map(item => item.label);
  }
  return thread.recipients || [];
};

const getRecipientEmails = (thread: CommunicationThread) => {
  if (thread.recipient_details?.length) {
    return thread.recipient_details
      .map(item => item.email?.trim())
      .filter((item): item is string => Boolean(item));
  }
  return (thread.recipients || []).filter(item => item.includes('@'));
};

const groupRecipients = (
  recipientDetails?: CommunicationRecipientDetail[] | null,
  excludedEmails: string[] = [],
) => {
  const groups: Record<CommunicationRecipientDetail['type'], CommunicationRecipientDetail[]> = {
    user: [],
    client: [],
    external: [],
  };

  const normalizedExcludedEmails = new Set(
    excludedEmails.map(email => email.trim().toLowerCase()).filter(Boolean),
  );

  (recipientDetails || []).forEach(item => {
    const normalizedEmail = item.email?.trim().toLowerCase() || '';
    if (normalizedEmail && normalizedExcludedEmails.has(normalizedEmail)) {
      return;
    }

    if (
      !groups[item.type].some(
        existing => existing.label === item.label && (existing.email || '') === (item.email || ''),
      )
    ) {
      groups[item.type].push(item);
    }
  });

  const internalIdentityKeys = new Set(
    [...groups.user, ...groups.client].flatMap(
      item =>
        [item.email?.trim().toLowerCase(), item.label.trim().toLowerCase()].filter(
          Boolean,
        ) as string[],
    ),
  );
  groups.external = groups.external.filter(item => {
    const normalizedEmail = item.email?.trim().toLowerCase() || '';
    const normalizedLabel = item.label.trim().toLowerCase();
    return !internalIdentityKeys.has(normalizedEmail) && !internalIdentityKeys.has(normalizedLabel);
  });

  return groups;
};

const formatRecipientsPreview = (items: string[]) => {
  if (!items.length) return '---';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]}, ${items[1]}`;
  return `${items[0]}, ${items[1]} +${items.length - 2}`;
};

const RecipientsCell = ({
  thread,
  excludedEmails = [],
}: {
  thread: CommunicationThread;
  excludedEmails?: string[];
}) => {
  const labels = getRecipientLabels(thread);
  const emails = getRecipientEmails(thread).filter(email => {
    const normalizedEmail = email.trim().toLowerCase();
    return !excludedEmails.some(item => item.trim().toLowerCase() === normalizedEmail);
  });
  const grouped = groupRecipients(thread.recipient_details, excludedEmails);
  const hasHover =
    grouped.user.length + grouped.client.length + grouped.external.length > 0 || labels.length > 1;

  if (!emails.length && !labels.length) {
    return <div className="text-gray-500">---</div>;
  }

  const previewSource = emails.length ? emails : labels;
  const preview = formatRecipientsPreview(previewSource);

  return (
    <div className="group/recipients relative min-w-[420px]">
      <div className="whitespace-nowrap text-gray-500 leading-5">{preview}</div>
      {hasHover && (
        <div className="invisible absolute left-0 top-full z-10 mt-2 w-72 rounded-lg border border-[#E5E7EB] bg-white p-3 text-sm text-[#111827] opacity-0 shadow-lg transition-all duration-150 group-hover/recipients:visible group-hover/recipients:opacity-100">
          {!!grouped.user.length && (
            <div className="mb-3 last:mb-0">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                Users
              </div>
              <div className="space-y-1">
                {grouped.user.map(item => (
                  <div key={`user-${item.label}-${item.email || ''}`} className="break-words">
                    <span>{item.label}</span>
                    {item.email && <span className="text-gray-500"> {item.email}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!!grouped.client.length && (
            <div className="mb-3 last:mb-0">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                Clients
              </div>
              <div className="space-y-1">
                {grouped.client.map(item => (
                  <div key={`client-${item.label}-${item.email || ''}`} className="break-words">
                    <span>{item.label}</span>
                    {item.email && <span className="text-gray-500"> {item.email}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!!grouped.external.length && (
            <div className="last:mb-0">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                External
              </div>
              <div className="space-y-1">
                {grouped.external.map(item => (
                  <div key={`external-${item.label}-${item.email || ''}`} className="break-words">
                    <span>{item.label}</span>
                    {item.email && item.email !== item.label && (
                      <span className="text-gray-500"> {item.email}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!thread.recipient_details?.length && (
            <div className="space-y-1">
              {(emails.length ? emails : labels).map(item => (
                <div key={item} className="break-words">
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CommunicationsWorkspace = ({ client }: CommunicationsWorkspaceProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { data: currentUser } = useCurrentUserQuery();
  const [params] = useSearchParams();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(client?.id || null);
  const [filters, setFilters] = useState<Filters>({});

  const { data: clientsData } = useClientsListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    false,
    { cacheKey: 'communications-filters-clients' },
  );

  const selectedBusinessId = params.get(BUSINESS_QUERY_PARAM);
  const activeBusiness = useMemo(
    () =>
      ((currentUser?.businesses || []) as unknown as CommunicationBusiness[]).find(
        ({ id }) => id === +(selectedBusinessId || '0'),
      ),
    [currentUser?.businesses, selectedBusinessId],
  );

  const clients = useMemo(
    () => (clientsData?.pages || []).flatMap(page => page.data) || [],
    [clientsData?.pages],
  );
  const clientFilterOptions = useMemo<SelectOption<number>[]>(
    () =>
      clients.map(item => ({
        value: item.id,
        label: item.name || item.email || `Client #${item.id}`,
      })),
    [clients],
  );

  const activeClient = useMemo(
    () =>
      client
        ? client
        : selectedClientId
        ? clients.find(item => item.id === selectedClientId)
        : undefined,
    [client, clients, selectedClientId],
  );
  const backendFilters = useMemo<CommunicationThreadFilters>(
    () => ({
      ...(client?.id || selectedClientId
        ? { client_id: client?.id || selectedClientId || undefined }
        : {}),
      ...(filters.text ? { search_text: filters.text } : {}),
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.sender ? { sender: filters.sender } : {}),
      ...(filters.recipient ? { participant: filters.recipient } : {}),
    }),
    [client?.id, filters.recipient, filters.sender, filters.text, filters.type, selectedClientId],
  );

  const { data, isLoading, fetchNextPage, hasNextPage } = useCommunicationsQuery(backendFilters);

  const threads = useMemo(() => (data?.pages || []).flatMap(page => page.data), [data?.pages]);

  const handleOpenCreateModal = useCallback(() => {
    openModal(CreateCommunicationModal, { client: activeClient });
  }, [activeClient, openModal]);

  const handleOpenHistoryModal = useCallback(() => {
    openModal(CreateCommunicationHistoryRecordModal, { client: activeClient });
  }, [activeClient, openModal]);

  const handleOpenThreadModal = useCallback(
    (thread: CommunicationThread) => {
      openModal(CommunicationThreadModal, {
        thread,
      });
    },
    [openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    return threads.map(thread => {
      const senderCell =
        thread.thread_type === 'external' ? (
          <div className="max-w-[150px]">
            {thread.sender_name && <div className="truncate text-sm">{thread.sender_name}</div>}
            {thread.sender_email && (
              <div className="truncate text-gray-600">{thread.sender_email}</div>
            )}
          </div>
        ) : thread.communication_origin === 'platform_notification' ? (
          'System'
        ) : thread.correspondence_type && thread.correspondence_type !== 'email' ? (
          '-'
        ) : (
          'Compli One'
        );

      return {
        onClick: () => handleOpenThreadModal(thread),
        cells: [
          {
            cell: <div className="truncate max-w-[40vw]">{thread.subject || '(No subject)'}</div>,
            className: '!pr-3',
          },
          {
            cell: senderCell,
            className: '!px-3',
          },
          {
            cell: dayjs(thread.last_date || thread.created_at).format('DD-MM-YYYY, HH:mm'),
            className: '!px-3',
          },
          {
            cell: <div>{getThreadDisplayType(thread)}</div>,
            className: '!px-3',
          },
          {
            cell: thread.status
              ? mapCommunicationStatusToBadge[thread.status] || thread.status
              : '---',
            className: '!px-3',
          },
          {
            cell: (
              <RecipientsCell
                thread={thread}
                excludedEmails={
                  activeBusiness?.correspondence_email ? [activeBusiness.correspondence_email] : []
                }
              />
            ),
            className: '!px-3',
          },
        ],
      };
    });
  }, [activeBusiness?.correspondence_email, handleOpenThreadModal, threads]);

  const handleChangeSearchFilter = useCallback((text: string) => {
    setFilters(current => (text ? { ...current, text } : omit(current, 'text')));
  }, []);

  const handleChangeTypeFilter = useCallback((type: CommunicationFilterType | null) => {
    setFilters(current => (type ? { ...current, type } : omit(current, 'type')));
  }, []);

  const handleChangeSenderFilter = useCallback((sender: string) => {
    setFilters(current => (sender ? { ...current, sender } : omit(current, 'sender')));
  }, []);

  const handleChangeRecipientsFilter = useCallback((recipient: string) => {
    setFilters(current => (recipient ? { ...current, recipient } : omit(current, 'recipient')));
  }, []);

  return (
    <div className="flex h-full min-h-[620px] flex-col">
      <div className="px-3 py-5 sm:px-6">
        {!client && (
          <>
            <h2 className="text-gray-500">Choose how you&apos;d like to send a message</h2>
            <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
              <InfoCard title="Send Via App" classes={{ content: 'space-y-3 pt-3' }}>
                <p className="text-sm">
                  Compose and send your message directly from within the platform. Ideal for quick
                  and secure communication with built-in tracking.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button className="w-fit" onClick={handleOpenCreateModal}>
                    New email
                  </Button>
                  <Button className="w-fit" variant="secondary" onClick={handleOpenHistoryModal}>
                    Report external communication
                  </Button>
                </div>
              </InfoCard>
              <InfoCard title="Copy Email Address" classes={{ content: 'space-y-3 pt-3' }}>
                <p
                  className="flex min-w-[220px] cursor-pointer items-center gap-1 text-sm"
                  onClick={copyTextToClipBoard.bind(
                    null,
                    activeBusiness?.correspondence_email || '',
                    {},
                  )}
                >
                  <span className="truncate">
                    {activeBusiness?.correspondence_email ||
                      'Tracking address available after business migration'}
                  </span>
                  {!!activeBusiness?.correspondence_email && (
                    <img src={CopyIcon} alt="copy" width={18} />
                  )}
                </p>
                <p className="text-sm">
                  Compose an email using your external tool (Outlook, Gmail, etc) and add this email
                  in CC to track your email correspondence through Compli One.
                </p>
              </InfoCard>
            </div>
          </>
        )}

        {!!client && (
          <div className="flex flex-wrap gap-3">
            <Button className="w-fit" onClick={handleOpenCreateModal}>
              New email
            </Button>
            <Button className="w-fit" variant="secondary" onClick={handleOpenHistoryModal}>
              Report external communication
            </Button>
          </div>
        )}

        <div className="mt-6 grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-5">
          <SearchField
            allowClear
            className="w-full"
            placeholder="Search by email text"
            onChange={handleChangeSearchFilter}
          />
          {!client && (
            <FilterItem
              type="select"
              label="Client"
              classes={{ label: 'min-w-[85px]', element: 'w-full' }}
              value={selectedClientId}
              options={clientFilterOptions}
              onChange={value => setSelectedClientId(value ?? null)}
            />
          )}
          <FilterItem
            type="select"
            label="Type"
            classes={{ label: 'min-w-[85px]', element: 'w-full' }}
            value={filters.type}
            options={typeOptions}
            onChange={handleChangeTypeFilter}
          />
          <FilterItem
            type="custom"
            label="Sender"
            classes={{ label: 'min-w-[85px]', element: 'w-full' }}
          >
            <SearchField
              plain
              allowClear
              placeholder="Name or email"
              onChange={handleChangeSenderFilter}
            />
          </FilterItem>
          <FilterItem type="custom" label="Participant" classes={{ element: 'w-full' }}>
            <SearchField
              plain
              allowClear
              placeholder="Name or email"
              onChange={handleChangeRecipientsFilter}
            />
          </FilterItem>
        </div>
      </div>

      <Table
        blockIdInfinite="scrollable"
        isSearchUsed={!isEmpty(filters)}
        isLoading={isLoading}
        fetchNextPageInfinite={fetchNextPage}
        hasNextPageInfinite={hasNextPage}
        withRounded={false}
        withBorder={false}
        withBorderX={false}
        className="mt-3 [&_th]:text-xs [&_td:not(.absolute)]:text-xs [&_td.absolute]:!top-[-60px]"
        items={bodyItems}
        headItems={headItems}
      />
    </div>
  );
};

export default CommunicationsWorkspace;
